import { useState } from "react";
import { HttpError, request } from "../../../api/http";
import {
  IconCard,
  IconCheck,
  IconCopy,
  IconFolder,
  IconLink,
  IconWhatsApp,
} from "../../../components/Icons";
import { useAuth } from "../../../context/AuthContext";
import { LeadStatus, OnboardingRow } from "../../../types/onboarding";
import FileExplorer from "./FileExplorer";
import StatusChangeButton from "./StatusChangeButton";
import WhatsAppChat from "./WhatsAppChat";

const STRIPE_DASHBOARD = "https://dashboard.stripe.com/customers/";

type Props = {
  row: OnboardingRow;
  onStatusChange: (newStatus: LeadStatus) => void;
};

export default function ActionButtons({ row, onStatusChange }: Props) {
  const { token } = useAuth();
  const [copied, setCopied] = useState(false);
  const [filesOpen, setFilesOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpResult, setOtpResult] = useState<
    | { kind: "ok"; url: string; copied: boolean }
    | { kind: "error"; message: string }
    | null
  >(null);

  const handleCopySignature = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!row.signatureLink) return;
    try {
      await navigator.clipboard.writeText(row.signatureLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API blocked
    }
  };

  const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

  const handleGenerateOtp = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (otpBusy) return;
    setOtpBusy(true);
    setOtpResult(null);
    try {
      const { url } = await request<{ url: string }>(
        `/api/admin/operations/leads/${row.leadUlid}/otp-url`,
        { method: "POST", token, body: {} },
      );
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        // clipboard blocked — toast still shows the URL for manual copy
      }
      setOtpResult({ kind: "ok", url, copied });
    } catch (err) {
      setOtpResult({
        kind: "error",
        message:
          err instanceof HttpError
            ? `HTTP ${err.status}`
            : "No se pudo generar el OTP",
      });
    } finally {
      setOtpBusy(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        gap: "0.3rem",
        justifyContent: "flex-end",
        position: "relative",
      }}
      onClick={stopPropagation}
    >
      <a
        href={
          row.stripeCustomerId ? `${STRIPE_DASHBOARD}${row.stripeCustomerId}` : "#"
        }
        target="_blank"
        rel="noopener noreferrer"
        title={
          row.stripeCustomerId
            ? `Abrir ${row.stripeCustomerId} en Stripe`
            : "Sin Stripe ID"
        }
        aria-disabled={!row.stripeCustomerId}
        style={{
          ...iconButtonStyle,
          opacity: row.stripeCustomerId ? 1 : 0.4,
          pointerEvents: row.stripeCustomerId ? "auto" : "none",
        }}
      >
        <IconCard />
      </a>
      <button
        type="button"
        onClick={handleCopySignature}
        disabled={!row.signatureLink}
        title={copied ? "¡Copiado!" : "Copiar Signature Link"}
        style={{
          ...iconButtonStyle,
          background: copied ? "#1a7f37" : iconButtonStyle.background,
          color: copied ? "white" : iconButtonStyle.color,
        }}
      >
        {copied ? <IconCheck /> : <IconCopy />}
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setChatOpen(true);
        }}
        disabled={!row.memberPhone}
        title={row.memberPhone ? `WhatsApp: ${row.memberPhone}` : "Sin telefono"}
        style={{
          ...iconButtonStyle,
          opacity: row.memberPhone ? 1 : 0.4,
          pointerEvents: row.memberPhone ? "auto" : "none",
          color: row.memberPhone ? "#25d366" : iconButtonStyle.color,
        }}
      >
        <IconWhatsApp />
      </button>
      <button
        type="button"
        onClick={handleGenerateOtp}
        disabled={otpBusy}
        title={
          otpBusy
            ? "Generando OTP…"
            : "Generar enlace OTP (copia al portapapeles)"
        }
        style={{
          ...iconButtonStyle,
          color: "#0069d9",
          opacity: otpBusy ? 0.6 : 1,
          cursor: otpBusy ? "wait" : "pointer",
        }}
      >
        <IconLink />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setFilesOpen(true);
        }}
        title="Ver archivos del lead"
        style={iconButtonStyle}
      >
        <IconFolder />
      </button>
      <StatusChangeButton
        leadUlid={row.leadUlid}
        currentStatus={row.status}
        onStatusChange={onStatusChange}
      />
      {filesOpen && (
        <FileExplorer
          leadId={row.leadId}
          leadUlid={row.leadUlid}
          customer={row.customer}
          currentStatus={row.status}
          onStatusChange={onStatusChange}
          onClose={() => setFilesOpen(false)}
        />
      )}
      {chatOpen && row.memberPhone && (
        <WhatsAppChat
          phone={row.memberPhone}
          customerName={row.customer}
          onClose={() => setChatOpen(false)}
        />
      )}
      {otpResult && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            setOtpResult(null);
          }}
          title="Cerrar"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            zIndex: 30,
            background: otpResult.kind === "ok" ? "#e7f1ff" : "#fde2e2",
            color: otpResult.kind === "ok" ? "#0a58ca" : "#b32424",
            padding: "0.45rem 0.7rem",
            borderRadius: "0.45rem",
            fontSize: ".75rem",
            maxWidth: 360,
            boxShadow: "0 4px 10px rgba(0,0,0,.12)",
            cursor: "pointer",
            wordBreak: "break-all",
            whiteSpace: "normal",
          }}
        >
          {otpResult.kind === "ok" ? (
            <>
              <div style={{ fontWeight: 700, marginBottom: "0.2rem" }}>
                {otpResult.copied
                  ? "OTP copiado al portapapeles"
                  : "OTP generado (cópialo manualmente)"}
              </div>
              <a
                href={otpResult.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                style={{ color: "#0a58ca", textDecoration: "underline" }}
              >
                {otpResult.url}
              </a>
            </>
          ) : (
            otpResult.message
          )}
        </div>
      )}
    </div>
  );
}

const iconButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "1.9rem",
  height: "1.9rem",
  padding: 0,
  borderRadius: "0.4rem",
  background: "#f8f9fb",
  color: "#1f2933",
  border: "1px solid #dee2e6",
  fontSize: ".95rem",
  cursor: "pointer",
  textDecoration: "none",
};
